import type { IconWeight } from "@phosphor-icons/react"
import { twMerge } from "tailwind-merge"
import Container from "../common/Container"
import MarketingHeading from "../common/MarketingHeading"

interface ProductPageHeaderProps {
  icon: React.ComponentType<{
    className?: string
    weight?: IconWeight
  }>
  title: string
  subtitle: string | React.ReactNode
  ctaButtons: React.ReactNode
  backgroundImageUrl?: string
  whoWeServeText: string
  simpleWhoWeServeText?: string
}

function ProductPageHeader({
  icon: Icon,
  title,
  subtitle,
  ctaButtons,
  backgroundImageUrl,
  whoWeServeText,
  simpleWhoWeServeText,
}: ProductPageHeaderProps) {
  const formattedWhoWeServeText =
    simpleWhoWeServeText && simpleWhoWeServeText.length > 0
      ? `For ${simpleWhoWeServeText}`
      : simpleWhoWeServeText || ""

  return (
    <Container className="w-full mt-2 lg:mt-[60px] relative">
      <div className="absolute top-0 left-0 w-full px-4 mx-auto pointer-events-none">
        <div
          className={twMerge(
            "w-full max-w-[1440px] mx-auto h-[300px] lg:h-[630px] rounded-t-[32px] overflow-hidden opacity-30",
            !backgroundImageUrl ? "bg-neutral-300 dark:bg-neutral-600" : "",
          )}
          style={{
            background: backgroundImageUrl ? `url('${backgroundImageUrl}')` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
          }}
        />
      </div>
      <div className="flex flex-col items-center gap-8 max-w-full relative z-[1] pt-[160px] lg:pt-[260px]">
        <div className="w-full max-w-[1440px] mx-auto px-4">
          <div className="w-full h-full flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 lg:gap-12 xl:gap-16 px-4 md:px-8 lg:px-12 pb-8 md:pb-12 lg:pb-16">
            {/* Left side: Icon, title, subtitle, who we serve text, buttons */}
            <div className="flex flex-col items-start gap-4 md:gap-5 lg:gap-6 flex-shrink-0">
              {/* Icon with glow */}
              <div className="relative mb-1">
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-white/20 blur-xl" />
                {/* Icon container */}
                <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-black/3 dark:bg-white/10 backdrop-blur-sm border border-black/[0.08] dark:border-white/20 flex items-center justify-center">
                  <Icon
                    className="w-8 h-8 md:w-10 md:h-10 text-fg-default dark:text-white"
                    weight="fill"
                  />
                </div>
              </div>

              {/* Title */}
              <MarketingHeading
                level={1}
                className="text-[40px] md:text-[56px] lg:text-[64px] xl:text-[70px] leading-none tracking-tighter text-left"
              >
                {title}
              </MarketingHeading>

              {/* Subtitle */}
              <div
                className="text-base md:text-lg leading-relaxed text-fg-secondary text-left max-w-2xl text-balance"
                style={{ background: "transparent", textShadow: "none" }}
              >
                {subtitle}
              </div>

              {/* Who We Serve Text */}
              {formattedWhoWeServeText && (
                <p className="text-base text-fg-secondary leading-relaxed text-left text-balance">
                  {formattedWhoWeServeText}
                </p>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-row items-center gap-3 mt-2">{ctaButtons}</div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}

export default ProductPageHeader
